if (!customElements.get('wlsn-device-support')) {
  class WLSNDeviceSupport extends HTMLElement {
    constructor() {
      super();

      this.selectors = {
        tabs: this.querySelectorAll('.js-device-support-tab'),
        contents: this.querySelectorAll('.js-device-support-content'),
      };

      this.onTabClick = this.onTabClick.bind(this);
    }

    /**
     * @description Add event listeners
     */
    connectedCallback() {
      this.selectors.tabs.forEach((tab) => tab.addEventListener('click', this.onTabClick));
      if ('IntersectionObserver' in window) {
        this.posterObserver = new IntersectionObserver((entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            observer.unobserve(entry.target);
            this.upgradePoster(entry.target);
          });
        }, { rootMargin: '300px' });
        this.querySelectorAll('wlsn-youtube-video .wlsn-youtube-video__poster img').forEach((image) => {
          this.posterObserver.observe(image);
        });
      }
    }

    /**
     * @description Remove event listeners
     */
    disconnectedCallback() {
      this.selectors.tabs.forEach((tab) => tab.removeEventListener('click', this.onTabClick));
      this.posterObserver?.disconnect();
    }

    upgradePoster(image) {
      const youtubeId = image.closest('wlsn-youtube-video')?.dataset.youtubeId;
      if (!/^[A-Za-z0-9_-]{11}$/.test(youtubeId || '')) return;

      const candidates = [
        { size: 'maxresdefault', minimumWidth: 1000 },
        { size: 'sddefault', minimumWidth: 600 },
      ];
      const tryCandidate = (index) => {
        if (index >= candidates.length) return;
        const candidate = candidates[index];
        const probe = new Image();
        probe.onload = () => {
          if (probe.naturalWidth < candidate.minimumWidth) {
            tryCandidate(index + 1);
            return;
          }
          if (!image.isConnected) return;
          image.src = probe.src;
          image.width = probe.naturalWidth;
          image.height = probe.naturalHeight;
        };
        probe.onerror = () => tryCandidate(index + 1);
        probe.src = `https://i.ytimg.com/vi/${youtubeId}/${candidate.size}.jpg`;
      };
      tryCandidate(0);
    }

    /**
     * @param {MouseEvent} event
     * @description Open the clicked tab, or close it if it is already active
     */
    onTabClick(event) {
      const tab = event.currentTarget;
      const isActive = tab.getAttribute('aria-expanded') === 'true';

      this.closeAll();

      if (!isActive) {
        this.open(tab);
        this.scrollToContent(tab);
      }
    }

    /**
     * @param {HTMLElement} tab
     * @description Open the content of the given tab
     */
    open(tab) {
      const content = this.querySelector(`.js-device-support-content[data-tab-id="${tab.dataset.tabId}"]`);

      tab.setAttribute('aria-expanded', 'true');

      if (content) {
        content.hidden = false;
      }
    }

    /**
     * @param {HTMLElement} tab
     * @description Smoothly scroll to the opened content of the given tab
     */
    scrollToContent(tab) {
      const content = this.querySelector(`.js-device-support-content[data-tab-id="${tab.dataset.tabId}"]`);

      if (!content) return;

      requestAnimationFrame(() => {
        content.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    /**
     * @description Close all tabs and pause their videos (uploaded and YouTube)
     */
    closeAll() {
      this.selectors.tabs.forEach((tab) => tab.setAttribute('aria-expanded', 'false'));

      this.selectors.contents.forEach((content) => {
        if (content.hidden) return;

        content.hidden = true;
        content.querySelectorAll('wlsn-video').forEach((videoElement) => {
          videoElement.querySelector('video')?.pause();
          videoElement.classList.remove('playing');
        });
        content.querySelectorAll('wlsn-youtube-video').forEach((youtubeElement) => youtubeElement.pause?.());
      });
    }
  }

  customElements.define('wlsn-device-support', WLSNDeviceSupport);
}
